import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import payrollService from '../../../services/payrollService';
import NotificationToast from '../../../components/ui/NotificationToast';

const EarningsBreakdownModal = ({ isOpen, onClose, payrollRecordId, employeeName, onUpdate }) => {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEarning, setEditingEarning] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const earningTypes = [
    { value: 'overtime', label: 'Overtime' },
    { value: 'bonus', label: 'Bonus' },
    { value: 'allowance', label: 'Allowance' },
    { value: 'incentive', label: 'Incentive' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (isOpen && payrollRecordId) {
      loadEarnings();
    }
  }, [isOpen, payrollRecordId]);

  const loadEarnings = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await payrollService.getEarningsBreakdown(payrollRecordId);
      if (result.success) {
        setEarnings(result.data || []);
      } else {
        setError(result.error || 'Failed to load earnings');
      }
    } catch (err) {
      setError('Failed to load earnings');
      console.error('Error loading earnings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingEarning(null);
    setShowAddModal(true);
  };

  const handleEdit = (earning) => {
    setEditingEarning(earning);
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this earning entry?')) return;
    
    try {
      const result = await payrollService.deleteEarning(id);
      if (result.success) {
        setNotification({ message: 'Earning entry deleted successfully', type: 'success' });
        loadEarnings();
        onUpdate?.();
      } else {
        setNotification({ message: result.error || 'Failed to delete earning entry', type: 'error' });
      }
    } catch (err) {
      setNotification({ message: 'Failed to delete earning entry', type: 'error' });
    }
  };

  const handleSave = async (earningData) => {
    try {
      let result;
      if (editingEarning) {
        result = await payrollService.updateEarning(editingEarning.id, earningData);
      } else {
        result = await payrollService.createEarning({
          ...earningData,
          payroll_record_id: payrollRecordId
        });
      }

      if (result.success) {
        setNotification({ 
          message: `Earning entry ${editingEarning ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        setShowAddModal(false);
        setEditingEarning(null);
        loadEarnings();
        onUpdate?.();
      } else {
        setNotification({ message: result.error || 'Failed to save earning entry', type: 'error' });
      }
    } catch (err) {
      setNotification({ message: 'Failed to save earning entry', type: 'error' });
    }
  };

  const filteredEarnings = filterType === 'all' 
    ? earnings 
    : earnings.filter(e => e.earning_type === filterType);

  const totalAmount = filteredEarnings.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Earnings Breakdown</h2>
                <p className="text-sm text-muted-foreground mt-1">{employeeName}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleAdd} iconName="Plus" iconPosition="left">
                  Add Earning
                </Button>
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-4">
              <Select
                value={filterType}
                onValueChange={setFilterType}
                options={[
                  { value: 'all', label: 'All Types' },
                  ...earningTypes
                ]}
                className="w-48"
              />
              <div className="flex-1 text-right">
                <span className="text-sm text-muted-foreground">Total: </span>
                <span className="text-lg font-semibold text-foreground">{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <NotificationToast
              message={notification?.message}
              type={notification?.type}
              onDismiss={() => setNotification(null)}
            />

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading earnings...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-destructive">{error}</p>
                <Button variant="outline" onClick={loadEarnings} className="mt-4">
                  Retry
                </Button>
              </div>
            ) : filteredEarnings.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                <p className="text-muted-foreground">No earnings found</p>
                <Button variant="outline" onClick={handleAdd} className="mt-4">
                  Add First Earning
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEarnings.map((earning) => (
                  <div
                    key={earning.id}
                    className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            earning.earning_type === 'overtime' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                            earning.earning_type === 'bonus' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                            earning.earning_type === 'allowance' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                            earning.earning_type === 'incentive' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                          }`}>
                            {earningTypes.find(t => t.value === earning.earning_type)?.label || earning.earning_type}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {new Date(earning.earning_date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">{formatCurrency(earning.amount)}</p>
                        {earning.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{earning.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(earning)}
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(earning.id)}
                          className="text-destructive"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <EarningFormModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingEarning(null);
          }}
          earning={editingEarning}
          onSave={handleSave}
          earningTypes={earningTypes}
        />
      )}
    </>
  );
};

// Earning Form Modal Component
const EarningFormModal = ({ isOpen, onClose, earning, onSave, earningTypes }) => {
  const [formData, setFormData] = useState({
    earning_type: earning?.earning_type || 'overtime',
    earning_date: earning?.earning_date || new Date().toISOString().split('T')[0],
    amount: earning?.amount || '',
    notes: earning?.notes || ''
  });
  const [error, setError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.earning_type || !formData.earning_date || !formData.amount) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    onSave({
      earning_type: formData.earning_type,
      earning_date: formData.earning_date,
      amount: parseFloat(formData.amount),
      notes: formData.notes || null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-background rounded-lg w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-foreground">
              {earning ? 'Edit Earning' : 'Add Earning'}
            </h3>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Earning Type *
            </label>
            <Select
              value={formData.earning_type}
              onValueChange={(value) => setFormData({ ...formData, earning_type: value })}
              options={earningTypes}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Earning Date *
            </label>
            <Input
              type="date"
              value={formData.earning_date}
              onChange={(e) => setFormData({ ...formData, earning_date: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Amount (₹) *
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              rows="3"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {earning ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EarningsBreakdownModal;









